import { useCallback, useEffect, useState } from 'react';

/**
 * Which edges of a scroll container are currently hiding content.
 *
 * A dense pane clips rows at 11px mono, and a row clipped halfway renders as a
 * band of half-glyphs that reads as a rendering fault rather than as "there is
 * more". The caller stamps this on the scroller and fades the clipped edge, so
 * the boundary always says which way the rest of the record lies.
 *
 * The handle is a CALLBACK ref, not an object ref: these panes swap their whole
 * subtree when the thing they show changes — the inspector renders a different
 * element for "nothing selected" than for a command — and an effect keyed on a
 * `useRef` box would have measured the node that existed at mount and never
 * heard about its replacement.
 */
export type ScrollEdge = 'none' | 'top' | 'bottom' | 'both';

export function useScrollEdges<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  edge: ScrollEdge;
} {
  const [node, setNode] = useState<T | null>(null);
  const [edge, setEdge] = useState<ScrollEdge>('none');
  const ref = useCallback((n: T | null) => setNode(n), []);

  useEffect(() => {
    if (node === null) {
      setEdge('none');
      return undefined;
    }
    const measure = () => {
      /* 1px of slack: sub-pixel layout routinely leaves scrollHeight a
         fraction above clientHeight on a container that cannot scroll. */
      const above = node.scrollTop > 1;
      const below = node.scrollTop + node.clientHeight < node.scrollHeight - 1;
      setEdge(above && below ? 'both' : above ? 'top' : below ? 'bottom' : 'none');
    };
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    /* The pane resizes (the rail is flexible) and its content changes (a
       different command is selected, a group opens). Both change what is
       clipped; neither fires the other's observer. `characterData` is left out
       deliberately — the ages inside these panes tick every second and
       re-measuring on each of them would be a MutationObserver storm for a
       value that cannot have changed what is clipped. */
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    const mo = new MutationObserver(measure);
    mo.observe(node, { childList: true, subtree: true });
    return () => {
      node.removeEventListener('scroll', measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [node]);

  return { ref, edge };
}
